// Colorizes the standard human-readable `tofu show` plan text by line prefix.
//
// The backend serves the plain-text plan (the familiar "Terraform will perform
// the following actions … Plan: N to add, N to change, N to destroy" output)
// rather than raw plan.json. Each line is categorized by its leading diff
// marker (ignoring indentation) so TerraformPlanDialog can render it colorized
// with the app's statusHues tokens, themed for light/dark.

import { statusHues } from '../theme/statusTokens';

// No amber exists in the sticker palette; statusHues.pending (#dd5b00) is orange
// and reserved for replace. This goldenrod amber keeps `~` update distinct.
export const planUpdateHue = '#b8860b';

export interface PlanLine {
  text: string;
  color?: string;
  bold?: boolean;
}

const verbColor = (verb: string): string | undefined => {
  switch (verb) {
    case 'created':
      return statusHues.success;
    case 'updated':
      return planUpdateHue;
    case 'destroyed':
      return statusHues.failed;
    case 'replaced':
      return statusHues.pending;
    default:
      return undefined; // "read" — bold, but no color
  }
};

const colorizeLine = (line: string): PlanLine => {
  const trimmed = line.trimStart();

  if (/^Plan:/.test(trimmed)) {
    return { text: line, bold: true };
  }

  const header = /^#.*\b(?:will|must) be (created|updated|destroyed|read|replaced)\b/.exec(trimmed);
  if (header) {
    return { text: line, bold: true, color: verbColor(header[1]) };
  }

  if (/^[-+]\/[-+]\s/.test(trimmed)) {
    return { text: line, color: statusHues.pending };
  }
  if (/^\+\s/.test(trimmed)) {
    return { text: line, color: statusHues.success };
  }
  if (/^-\s/.test(trimmed)) {
    return { text: line, color: statusHues.failed };
  }
  if (/^~\s/.test(trimmed)) {
    return { text: line, color: planUpdateHue };
  }

  return { text: line };
};

export function planColorize(planText: string): PlanLine[] {
  return planText.split('\n').map(colorizeLine);
}
